import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilSimple, ClockCounterClockwise } from '@phosphor-icons/react';
import api from '@/lib/api';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const PREF_KEY = 'smsuite_bill_history_show';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const STATUS_CLS = {
  paid: 'bg-[#DCFCE7] text-[#15803D]', partial: 'bg-[#FEF3C7] text-[#B45309]',
  unpaid: 'bg-[#FEE2E2] text-[#B91C1C]', credit: 'bg-[#FEE2E2] text-[#B91C1C]',
  draft: 'bg-[#E0E7FF] text-[#4338CA]', overpaid: 'bg-[#EDE9FE] text-[#6D28D9]',
};

export function BillHistoryCard({ customerId, excludeId, refreshKey, beforeOpen }) {
  const [show, setShow] = useState(() => localStorage.getItem(PREF_KEY) !== '0');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!customerId || !show) { setRows([]); return; }
    setLoading(true);
    api.get(`/customers/${customerId}/bill-history`)
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [customerId, show, refreshKey]);

  if (!customerId) return null;
  const toggle = (v) => { setShow(!!v); localStorage.setItem(PREF_KEY, v ? '1' : '0'); };
  const list = rows.filter(r => r.id !== excludeId);
  const open = (r) => (beforeOpen && !beforeOpen()) ? null : (r.kind === 'draft' ? window.location.assign(`/invoices/create?draft=${r.id}`) : navigate(`/invoices/edit/${r.id}`));

  return (
    <div className="mt-5 rounded-xl border border-[#E4E4E7] bg-white" data-testid="bill-history-card">
      <label className="flex items-center gap-2 px-4 py-3 cursor-pointer select-none border-b border-[#F4F4F5]">
        <Checkbox checked={show} onCheckedChange={toggle} data-testid="bill-history-toggle" />
        <ClockCounterClockwise size={15} weight="duotone" className="text-[#4F46E5]" />
        <span className="text-xs uppercase tracking-[0.15em] font-bold text-[#0A0A0A]">Bill history</span>
        {show && <span className="text-[11px] text-[#A1A1AA]">({list.length} previous bill{list.length === 1 ? '' : 's'}, latest first)</span>}
      </label>
      {show && (
        loading ? <p className="px-4 py-4 text-xs text-[#A1A1AA] italic">Loading bills…</p>
        : list.length === 0 ? <p className="px-4 py-4 text-xs text-[#A1A1AA] italic" data-testid="bill-history-empty">No previous bills for this customer.</p>
        : (
          <div className="max-h-[320px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  {['Date', 'Bill #', 'Services', 'Total', 'Paid', 'Due', 'Status', ''].map((h, i) => (
                    <TableHead key={i} className={`text-[10px] uppercase tracking-[0.12em] font-semibold ${i >= 3 && i <= 5 ? 'text-right' : ''}`}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map(r => (
                  <TableRow key={`${r.kind}-${r.id}`} className="border-[#F4F4F5]" data-testid={`bill-history-row-${r.id}`}>
                    <TableCell className="text-xs text-[#52525B] whitespace-nowrap">{r.date ? new Date(r.date).toLocaleDateString('en-IN') : '—'}</TableCell>
                    <TableCell className="text-xs font-mono font-semibold text-[#3B82F6] whitespace-nowrap">{r.number}</TableCell>
                    <TableCell className="text-xs text-[#52525B] max-w-[320px] truncate" title={r.services}>{r.services || '—'}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{fmt(r.total)}</TableCell>
                    <TableCell className="text-xs text-right font-mono text-[#10B981]">{fmt(r.paid)}</TableCell>
                    <TableCell className={`text-xs text-right font-mono ${r.balance > 0 ? 'text-[#EF4444] font-semibold' : 'text-[#A1A1AA]'}`}>{fmt(r.balance)}</TableCell>
                    <TableCell><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${STATUS_CLS[r.status] || 'bg-[#F4F4F5] text-[#52525B]'}`}>{r.status}</span></TableCell>
                    <TableCell className="text-right">
                      <button type="button" onClick={() => open(r)} title="Open & edit this bill" className="p-1 rounded text-[#52525B] hover:bg-[#F4F4F5] hover:text-[#0A0A0A]" data-testid={`bill-history-open-${r.id}`}>
                        <PencilSimple size={14} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      )}
    </div>
  );
}
